import React, { useMemo } from 'react'
import { Package, Truck, MoveRight, FileText } from 'lucide-react'
import { GensetMark } from './GensetMark'
import { ContainerTask } from '../../types/container'
import { statusConfig, taskTypeConfig } from '../../data/dispatchData'
import { getRouteStops } from '../dispatch/RouteDisplay'
import { RouteEndpointCell } from '../../pages/dispatch/components/RouteEndpointCell'

/** Only reached when a stop carries no short name — see `stops` below. */
const defaultPortAbbreviation = (port: string) => port.substring(0, 3).toUpperCase()
const defaultWarehouseAbbreviation = (warehouse: string) => warehouse

/**
 * 状态灯只有三档：还没开跑、正在跑、跑完了。
 *
 * 全局那份 statusConfig 有十六个状态、十六种颜色 —— 紫、天蓝、靛、青、柠檬、
 * 蓝绿……堆在一张卡片上，一列卡片扫下来像一排色卡，而调度在这一屏上真正要分的
 * 只有三件事：这活还没动、司机在路上、已经完事。十六种颜色分不出这三件事，
 * 三种能。
 *
 * 深浅一律 100/800/200，所以三个颜色只在色相上不同，没有哪一个天生比别的抢眼。
 * 具体的状态名照旧写在牌子上 —— 收掉的是颜色，不是信息。
 */
type StatusBucket = 'pending' | 'running' | 'done' | 'closed'

const STATUS_BUCKET: Record<string, StatusBucket> = {
  // 还没开跑 —— 包括「正在派」的这一刻。
  'on-vessel': 'pending',
  'released': 'pending',
  'on-hold': 'pending',
  'pcc-hold': 'pending',
  'unassigned': 'pending',
  'assigned': 'pending',
  'paused': 'pending',
  // 司机手上正在跑的那一段，从提柜到还柜都算。
  'picking': 'running',
  'delivering': 'running',
  'on-stop': 'running',
  'loading': 'running',
  'loaded': 'running',
  'empty': 'running',
  'returning': 'running',
  'yard-stored': 'running',
  // 完事了。
  'completed': 'done',
  // 也结束了，但不是干完的 —— 不给绿色，那会读成「做完了」。
  'cancelled': 'closed',
  'uncompleted': 'closed',
}

/**
 * 一档颜色染整张卡片，不是只染那个小牌子。
 *
 * 这是司机卡片上草稿卡早就在做的事 —— 整张浅黄、表头深一档、路线条深一档、
 * 序号圆点最深 —— 隔着半屏也认得出哪几张是草稿。一个 11px 的小牌子做不到这个：
 * 得先看到那张卡片，再去找牌子，再读牌子上的字。
 *
 * 四组用的是同一套梯度（50 / 100 / 200 / 300），只换色相，所以没有哪一档天生
 * 比别的重。
 */
interface StatusTheme {
  /** 表头左边那个状态牌。 */
  pill: string
  /** 整张卡片的底色和描边。 */
  card: string
  /** 表头那一条。 */
  header: string
  /** 路线那一条。 */
  routeRow: string
  /** 路线左边那个序号圆点 —— 一列里最深的那一点。 */
  numberDisc: string
}

const STATUS_THEME: Record<StatusBucket, StatusTheme> = {
  pending: {
    pill: 'bg-amber-200/70 text-amber-800',
    card: 'bg-amber-50 border-amber-300',
    header: 'bg-amber-100/60 border-amber-200',
    routeRow: 'bg-amber-100/60',
    numberDisc: 'bg-amber-200 text-amber-800',
  },
  running: {
    pill: 'bg-blue-200/70 text-blue-800',
    card: 'bg-blue-50 border-blue-300',
    header: 'bg-blue-100/60 border-blue-200',
    routeRow: 'bg-blue-100/60',
    numberDisc: 'bg-blue-200 text-blue-800',
  },
  done: {
    pill: 'bg-emerald-200/70 text-emerald-800',
    card: 'bg-emerald-50 border-emerald-300',
    header: 'bg-emerald-100/60 border-emerald-200',
    routeRow: 'bg-emerald-100/60',
    numberDisc: 'bg-emerald-200 text-emerald-800',
  },
  closed: {
    pill: 'bg-slate-200/70 text-slate-600',
    card: 'bg-slate-50 border-slate-300',
    header: 'bg-slate-100/60 border-slate-200',
    routeRow: 'bg-slate-100/60',
    numberDisc: 'bg-slate-200 text-slate-600',
  },
}

const statusTheme = (status: string): StatusTheme =>
  STATUS_THEME[STATUS_BUCKET[status] ?? 'closed']

/**
 * 同一张卡，什么颜色都不带。
 *
 * 状态染色是为「扫一整天」设计的：司机卡上二十张卡片摊在那儿，一眼看出哪几张还
 * 没跑、哪几张跑完了，比读字快。派送确认窗口不是这个场景 —— 那儿一次只看几张
 * 卡，要回答的只有「这活有没有事、跟谁绑一趟」，状态色反而和「红=去不了、橙=
 * 时间紧、青=正在配对」抢同一双眼睛。调度反馈就是「颜色太多」。
 *
 * 所以那边整张卡走灰白，状态照旧写在表头的牌子上（收掉的是颜色，不是信息），
 * 颜色全部让给出事的那几张。司机卡不受影响：它不传 palette。
 */
const QUIET_THEME: StatusTheme = {
  pill: 'bg-slate-100 text-slate-600',
  card: 'bg-white border-slate-300',
  header: 'bg-slate-50 border-slate-200',
  routeRow: 'bg-slate-50',
  numberDisc: 'bg-slate-200 text-slate-700',
}

/** 右上角「任务类型 / 子类型」那个牌子在安静档下的样子。 */
const QUIET_TYPE_PILL = 'bg-slate-100 text-slate-600'

/**
 * The task card that sits inside a driver card.
 *
 * Purely presentational and shared by every page that lists a driver's day —
 * the dispatch board (wrapped in DraggableAssignedTask for drag-to-reassign),
 * Driver Tasks and Driver Management. Those three used to each render their own
 * variant, so the same task read differently depending on which page you were
 * on. Anything that changes how a task is summarised belongs here, once.
 *
 * Drag behaviour is deliberately NOT part of this component: the draggable
 * wrapper passes its ref/listeners/style through `...rest` onto the root, so the
 * non-draggable pages render byte-identical markup without pulling in dnd-kit.
 */
export interface AssignedTaskCardProps extends React.HTMLAttributes<HTMLDivElement> {
  container: ContainerTask
  /** Which run of the driver's day this is — shown in the disc on the route row. */
  taskNumber: number
  /** Fallbacks, only reached when a stop has no short name of its own. */
  getPortAbbreviation?: (port: string) => string
  getWarehouseAbbreviation?: (warehouse: string) => string
  /** Opt-in: renders the details button in the body when provided. */
  onTaskDetailClick?: (task: ContainerTask) => void
  /**
   * A full-width strip above the header: clashes, day flags, "someone else took
   * this". Above everything because a card in trouble should say so before it
   * says which card it is — and because anywhere lower it pushes the rest of the
   * card down by a line, so no two cards in a stack line up.
   */
  alerts?: React.ReactNode
  /**
   * Beside the details button, at the end of the identifier row. The review
   * panel puts its pairing chain here — next to the other thing on the card you
   * can press, rather than off on its own somewhere.
   */
  trailingSlot?: React.ReactNode
  /** Rides beside the container number — the review panel's "paired with" tag. */
  identifierSuffix?: React.ReactNode
  /**
   * Replaces the status pill on the left of the header. Drafts pass their own
   * badge here: a pending assignment has no server status worth showing, but it
   * still deserves the same header row as every other card rather than a
   * differently-shaped one.
   */
  statusSlot?: React.ReactNode
  /** Extra control pinned to the right of the header — the draft's remove button. */
  headerAction?: React.ReactNode
  /**
   * Overrides on the status theme, for a card that is not about its status.
   * The draft card on a driver card uses them: a pending assignment is not a
   * status, it is a proposal, so it wears amber and a dashed border whatever the
   * underlying task says.
   */
  headerClassName?: string
  routeRowClassName?: string
  taskNumberClassName?: string
  /**
   * `status`（默认）按任务状态染整张卡；`quiet` 全卡灰白，颜色留给卡上真正在报
   * 警的东西。见 `QUIET_THEME` —— 派送确认窗口是唯一用 quiet 的地方。
   */
  palette?: 'status' | 'quiet'
}

export const AssignedTaskCard = React.forwardRef<HTMLDivElement, AssignedTaskCardProps>(({
  container,
  taskNumber,
  getPortAbbreviation = defaultPortAbbreviation,
  getWarehouseAbbreviation = defaultWarehouseAbbreviation,
  onTaskDetailClick,
  alerts,
  trailingSlot,
  identifierSuffix,
  statusSlot,
  headerAction,
  headerClassName,
  routeRowClassName,
  taskNumberClassName,
  palette = 'status',
  className = '',
  ...rest
}, ref) => {
  // Every stop on the route — same resolution as the Route column (handles yard /
  // PCC / shipper origins, not just port→warehouse). A live-load through two
  // warehouses is four stops and draws as four; drawing only the two ends hid
  // the warehouses the driver actually loads at. Falls back to the old
  // abbreviation helpers when a node has no short name to resolve.
  //
  // Arrows always point right: stops come back in travel order, so a
  // direction-derived arrow would contradict the row's reading order for
  // empty-in / load-in.
  const stops = useMemo(() => {
    const resolved = getRouteStops(container)
    return resolved.map((stop, i) => ({
      ...stop,
      shortName: stop.shortName || (i === 0
        ? getPortAbbreviation(container.portName || '')
        : getWarehouseAbbreviation(container.warehouse || '')),
    }))
  }, [container, getPortAbbreviation, getWarehouseAbbreviation])

  const theme = palette === 'quiet' ? QUIET_THEME : statusTheme(container.status)

  const subtypeLabel = container.taskSubtype
    ? (container.taskSubtype === 'live-load' ? 'Live Load' : 'Drop')
    : ''

  return (
    <div
      ref={ref}
      className={`rounded-lg border transition-shadow duration-100 overflow-hidden ${theme.card} ${className}`}
      {...rest}
    >
      {alerts}

      {/* Task Header — status on the left, type/subtype on the right. */}
      <div className={`flex items-center justify-between gap-2 px-3 py-1.5 border-b ${headerClassName ?? theme.header}`}>
        <div className="flex items-center gap-2 min-w-0">
          {statusSlot ?? (
            <div className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${theme.pill}`}>
              {statusConfig[container.status]?.label || container.status}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
            palette === 'quiet'
              ? QUIET_TYPE_PILL
              : `${taskTypeConfig[container.taskType]?.bgColor || 'bg-slate-100'} ${
                  taskTypeConfig[container.taskType]?.textColor || 'text-slate-700'
                }`
          }`}>
            {taskTypeConfig[container.taskType]?.label || container.taskType}
            {subtypeLabel ? ` / ${subtypeLabel}` : ''}
          </div>
          {headerAction}
        </div>
      </div>

      {/* Task Body — route leads. Scanning this list, a dispatcher is checking
          where the driver is headed and whether the appointments hold; the
          container and chassis identifiers are lookup keys, so they follow. */}
      <div className="px-3 py-2.5">
        {/* Route + appointments — each time sits under the node it belongs to. */}
        <div className={`flex items-start gap-2 rounded-md px-2.5 py-1.5 ${routeRowClassName ?? theme.routeRow}`}>
          {/* Sits with the route rather than up in the header because it answers
              "when", which is what the stops and their times are about — and a
              filled disc makes the running order scannable down a stack. */}
          <span
            className={`flex-shrink-0 self-center w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center tabular-nums ${taskNumberClassName ?? theme.numberDisc}`}
            title={`Task ${taskNumber} of the day`}
          >
            {taskNumber}
          </span>
          {/* Wraps rather than squeezing: a four-stop route on a narrow board
              takes a second line instead of truncating every stop to nothing. */}
          <div className="flex items-start gap-2 flex-wrap min-w-0 flex-1">
            {stops.map((stop, i) => (
              <React.Fragment key={`${stop.type}-${stop.shortName}-${i}`}>
                {/* MoveRight is the long-shafted arrow; at w-6 the route direction
                    carries across the row instead of reading as punctuation. */}
                {i > 0 && (
                  <MoveRight className="w-6 h-4 text-slate-500 flex-shrink-0 self-center" aria-hidden="true" />
                )}
                <RouteEndpointCell endpoint={stop} quiet={palette === 'quiet'} />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Container & Chassis — the two numbers a dispatcher reads off this
            card and matches against a terminal screen, a phone call or a
            spreadsheet. They used to be set at the card's smallest size in its
            quietest greys, below a route drawn twice as large; the one string
            the whole card is about was the hardest thing on it to read.

            The box number leads and is the only thing here in ink of its own.
            Blue on purpose: red and amber already mean "this task is in
            trouble" and cyan is the one button, so blue reads as "identifier"
            rather than as a state or something to press. */}
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Package className="w-4 h-4 text-blue-500 flex-shrink-0" aria-hidden="true" />
            {/* 出口单在提柜之前根本没有柜号，这一格以前就是空的 —— 整张卡上没有
                一个号码认得出它是哪一票。订舱号顶上来，前面挂个 BKG 说明这不是
                柜号，免得被抄到码头界面里去。柜号一到，照旧显示柜号。 */}
            {container.containerNumber ? (
              <span className="text-sm font-mono font-bold tracking-tight text-blue-700 truncate">
                {container.containerNumber}
              </span>
            ) : container.bookingNumber ? (
              <span className="flex items-baseline gap-1 min-w-0">
                <span className="text-[10px] font-semibold text-slate-400 flex-shrink-0">BKG</span>
                <span className="text-sm font-mono font-bold tracking-tight text-blue-700 truncate">
                  {container.bookingNumber}
                </span>
              </span>
            ) : (
              <span className="text-sm font-mono font-bold tracking-tight text-blue-700 truncate" />
            )}
            {identifierSuffix}
          </div>
          <span className="text-slate-300 select-none">·</span>
          {/* Chassis keeps its icon even with nothing hooked up — the placeholder
              is what tells the dispatcher none is assigned yet.
              Same size as the box number now, a step quieter in colour: it is
              read just as often, but it is never the thing being looked up. */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Truck className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-mono font-semibold text-slate-700 truncate">
              {container.chassisNumber || '--'}
            </span>
            {container.chassisCompany && !container.chassisId && (
              // 安静档下不用橙：那是「时间紧」的颜色，而这只是底盘挂在谁名下。
              <span className={`text-[10px] px-1 rounded ${
                palette === 'quiet' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'
              }`}>{container.chassisCompany}</span>
            )}
            {/* 要哪种发电机 —— 和派单板任务行同一个组件、同一条规则。这张卡是
                派活的地方，漏在这里就等于挑司机的那一眼看不见它。 */}
            <GensetMark gensetType={container.gensetType} className="flex-shrink-0" />
          </div>
          {/* The card's buttons, together at the end of the row. Anything the
              dispatcher can press on this card is in one place, so there is one
              spot to look rather than a control tucked into each region. */}
          {(trailingSlot || onTaskDetailClick) && (
            <div className="ml-auto flex items-center gap-1 flex-shrink-0">
              {trailingSlot}
              {onTaskDetailClick && (
                <button
                  // On the dispatch board the whole card is a drag handle, so the
                  // press has to be kept away from the sensor or dnd-kit swallows
                  // it as a drag start. Harmless on the pages without drag.
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    onTaskDetailClick(container)
                  }}
                  className={`p-1 -mr-1 rounded transition-colors flex-shrink-0 ${
                    palette === 'quiet' ? 'hover:bg-slate-100' : 'hover:bg-blue-100'
                  }`}
                  title="View Task Details"
                  aria-label={`View details for task ${container.taskId || container.containerNumber}`}
                >
                  {/* 安静档下跟旁边那条锁链同色 —— 它们是同一种东西：这张卡上能按的。 */}
                  <FileText className={`w-3.5 h-3.5 ${palette === 'quiet' ? 'text-slate-400' : 'text-blue-500'}`} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

AssignedTaskCard.displayName = 'AssignedTaskCard'
